import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInButton, SignOutButton, useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import { getWarrantyStatus, getWarrantyColor } from './warrantyUtils';
import { parseDateInput } from './utils/dates';
import BuildOrdersPage from './BuildOrdersPage';
import LoadingSpinner from './components/LoadingSpinner';
import { getWaveSeries, seriesMap } from './waveSeries';
import { isTestGpv, normalizeRoleToken, isGpvRmaRole } from './roles';
import TestGpvBanner from './TestGpvBanner';
import ProductThumb from './components/ProductThumb';

// Dynamically import all product images from src/product-images


const API_BASE = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 50;

export default function MainPage() {
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState('');
  const [searchTags, setSearchTags] = useState([]);
  const [sortField, setSortField] = useState('scandate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState('all');
  const [testOnly, setTestOnly] = useState(false); // filter to productFamily:test
  const [deletingTest, setDeletingTest] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [anonServiceTag, setAnonServiceTag] = useState('');
  const [anonMessage, setAnonMessage] = useState('');
  const [anonLoading, setAnonLoading] = useState(false);
  const [anonCooldown, setAnonCooldown] = useState(0);
  const [anonSlowMessage, setAnonSlowMessage] = useState('');
  const navigate = useNavigate();
  const { user } = useUser();
  const { isSignedIn, getToken } = useAuth();
  const initialFetchRef = useRef(true);
  const lastSearchRef = useRef('');
  const cooldownTimerRef = useRef(null);
  const slowMessageTimerRef = useRef(null);

  // Each committed tag stores how it joins to the PREVIOUS tag:
  //   ","  → OR  (either matches)   ·   "+" → AND (both must match).
  // pendingOp is the operator that will apply to the NEXT committed tag.
  const [pendingOp, setPendingOp] = useState('OR');
  const sepFor = op => (op === 'AND' ? '+' : ',');

  // Rebuild the raw query string the backend parses (splits on "," then "+").
  const effectiveSearch = React.useMemo(() => {
    let q = '';
    searchTags.forEach((t, i) => { q += i === 0 ? t.value : `${sepFor(t.op)}${t.value}`; });
    const live = search.trim();
    if (live) q += (searchTags.length ? sepFor(pendingOp) : '') + live;
    return q;
  }, [searchTags, search, pendingOp]);

  const commitTag = (value, op) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSearchTags(prev => [...prev, { value: trimmed, op: prev.length === 0 ? 'OR' : op }]);
    setSearch('');
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    if (!/[+,]/.test(value)) { setSearch(value); return; }
    // Keep separators so each term knows which operator precedes it.
    const tokens = value.split(/([+,])/);
    let op = pendingOp;
    let remainder = '';
    const additions = [];
    tokens.forEach((tok, idx) => {
      if (tok === '+' || tok === ',') {
        op = tok === '+' ? 'AND' : 'OR';
      } else if (idx === tokens.length - 1) {
        remainder = tok;
      } else if (tok.trim()) {
        additions.push({ value: tok.trim(), op });
      }
    });
    if (additions.length) {
      setSearchTags(prev => {
        const next = [...prev];
        additions.forEach(a => next.push({ value: a.value, op: next.length === 0 ? 'OR' : a.op }));
        return next;
      });
    }
    setPendingOp(op);
    setSearch(remainder);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTag(search, pendingOp);
      setPendingOp('OR');
    } else if (e.key === 'Backspace' && !search && searchTags.length > 0) {
      setSearchTags(prev => prev.slice(0, -1));
    }
  };

  const removeTag = index => setSearchTags(prev => prev.filter((_, i) => i !== index));
  const slowMessages = React.useMemo(() => [
    'Grinding the gears a little...',
    'Warming up the radar...',
    'Asking the archive goblins...'
  ], []);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
      if (slowMessageTimerRef.current) {
        clearTimeout(slowMessageTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSignedIn) return;
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    if (slowMessageTimerRef.current) {
      clearTimeout(slowMessageTimerRef.current);
      slowMessageTimerRef.current = null;
    }
    setAnonCooldown(0);
    setAnonLoading(false);
    setAnonMessage('');
    setAnonServiceTag('');
    setAnonSlowMessage('');
  }, [isSignedIn]);

  const startAnonCooldown = useCallback(() => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    setAnonCooldown(10);
    const interval = setInterval(() => {
      setAnonCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    cooldownTimerRef.current = interval;
  }, []);

  const publicRoles = Array.isArray(user?.publicMetadata?.roles) ? user.publicMetadata.roles : null;
  const primaryRole = publicRoles && publicRoles.length > 0
    ? publicRoles[0]
    : user?.publicMetadata?.role ?? user?.role ?? 'guest';
  const displayRole = primaryRole == null ? 'guest' : String(primaryRole);
  const normalizedRole = normalizeRoleToken(displayRole) || 'guest';
  const isGpvRma = isGpvRmaRole(displayRole);
  const isGpv = normalizedRole === 'gpv';
  const isAdmin = normalizedRole === 'admin';
  const isService = normalizedRole.startsWith('service');
  const testGpvView = isTestGpv(user);
  const defaultView =
    testGpvView
      ? 'Test GPV'
      : isService
      ? 'Service'
      : isGpvRma
      ? 'GPV-RMA'
      : isGpv
      ? 'GPV Quality'
      : normalizedRole.startsWith('gpv')
      ? 'GPV Production'
      : isAdmin
      ? 'Admin'
      : 'guest';
  const [viewAs, setViewAs] = useState(defaultView);
  const currentView = isAdmin ? viewAs : defaultView;
  const showPocTestView = currentView === 'Test GPV';
  const showDeviceGrid = showPocTestView || currentView === 'Service';
  const fetchDevices = useCallback(
    async (reset = false, skipVal = 0, searchTerm = '', seriesVal = 'all', familyVal = '') => {
      reset ? setLoading(true) : setLoadingMore(true);
      try {
        const token = await getToken();
        if (!token) {
          console.warn('No token available for request');
          return;
        }

        const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skipVal) });
        if (searchTerm) params.set('search', searchTerm);
        if (seriesVal && seriesVal !== 'all') params.set('series', seriesVal);
        if (familyVal) params.set('productFamily', familyVal);
        const endpoint = showPocTestView ? `${API_BASE}/poc-test/devices` : `${API_BASE}/devices`;
        const res = await fetch(`${endpoint}?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }

        const data = await res.json();
        const payloadDevices = Array.isArray(data.devices) ? data.devices : [];
        let total = payloadDevices.length;
        if (typeof data.totalCount === 'number') {
          total = data.totalCount;
        } else if (typeof data.total === 'number') {
          total = data.total;
        }
        let filtered = payloadDevices.length;
        if (typeof data.filteredCount === 'number') {
          filtered = data.filteredCount;
        }

        setDevices(prev => (reset ? payloadDevices : [...prev, ...payloadDevices]));
        setTotalCount(total);
        setFilteredCount(filtered);

        if (reset) {
          const firstDevice = payloadDevices[0];
          const latestTimestamp = firstDevice ? (firstDevice.updatedAt || firstDevice.ingested_at || null) : null;
          setLastUpdate(latestTimestamp);
        }

        const reportedHasMore = typeof data.hasMore === 'boolean' ? data.hasMore : payloadDevices.length === PAGE_SIZE;
        const loadedAfter = Number(skipVal) + payloadDevices.length;
        // Pagination works for searches too — base "has more" on how many we've
        // loaded vs. the filtered total, not on whether a search is active.
        setHasMore(filtered > 0 ? loadedAfter < filtered : reportedHasMore);
      } catch (err) {
        console.error('Failed to fetch devices:', err);
      } finally {
        reset ? setLoading(false) : setLoadingMore(false);
      }
    },
    [getToken, showPocTestView]
  );

  const fetchNewDevices = useCallback(async () => {
    if (!lastUpdate || effectiveSearch || seriesFilter !== 'all' || testOnly) return;
    try {
      const token = await getToken();
      if (!token) return;
      const params = new URLSearchParams({ updatedAfter: lastUpdate, limit: String(PAGE_SIZE) });
      if (seriesFilter !== 'all') params.set('series', seriesFilter);
      const endpoint = showPocTestView ? `${API_BASE}/poc-test/devices` : `${API_BASE}/devices`;
      const res = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const payloadDevices = Array.isArray(data.devices) ? data.devices : [];
      if (payloadDevices.length > 0) {
        setDevices(prev => {
          const existing = new Set(prev.map(d => d._id));
          const newUnique = payloadDevices.filter(d => !existing.has(d._id));
          return [...newUnique, ...prev];
        });
        const firstDevice = payloadDevices[0];
        const latestTimestamp = firstDevice ? (firstDevice.updatedAt || firstDevice.ingested_at || null) : null;
        if (latestTimestamp) {
          setLastUpdate(latestTimestamp);
        }
        if (typeof data.totalCount === 'number') {
          setTotalCount(data.totalCount);
        }
        if (typeof data.filteredCount === 'number') {
          setFilteredCount(data.filteredCount);
        }
      }
    } catch (err) {
      console.error('Failed to fetch new devices:', err);
    }
  }, [getToken, lastUpdate, effectiveSearch, seriesFilter, showPocTestView, testOnly]);

  const handleReset = () => {
    setFadeOut(true);
    setTimeout(async () => {
      lastSearchRef.current = '';
      setSearch('');
      setSearchTags([]);
      setPendingOp('OR');
      setSeriesFilter('all');
      setTestOnly(false);
      await fetchDevices(true, 0, '', 'all', '');
      setFadeOut(false);
    }, 200);
  };

  // Delete a single productFamily:test device (server enforces test-only).
  const deleteTestDevice = async (id) => {
    if (!window.confirm('Delete this TEST device from the database? This cannot be undone.')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/devices/test/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { window.alert('Delete failed (only productFamily:test devices can be deleted).'); return; }
      setDevices(prev => prev.filter(d => d._id !== id));
      setFilteredCount(c => Math.max(0, c - 1));
      setTotalCount(c => Math.max(0, c - 1));
    } catch {
      window.alert('Delete failed. Please try again.');
    }
  };

  // Bulk-delete every productFamily:test device.
  const deleteAllTest = async () => {
    if (!window.confirm('Delete ALL productFamily "test" devices from the database? This cannot be undone.')) return;
    setDeletingTest(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/devices/test`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert('Delete failed.'); return; }
      window.alert(`Deleted ${data.deletedCount ?? 0} test device(s).`);
      await fetchDevices(true, 0, effectiveSearch, seriesFilter, testOnly ? 'test' : '');
    } catch {
      window.alert('Delete failed. Please try again.');
    } finally {
      setDeletingTest(false);
    }
  };

  const handleAnonymousSearch = async () => {
    const tag = anonServiceTag.trim();
    if (!tag) {
      setAnonMessage('Please enter your service tag.');
      return;
    }
    if (anonCooldown > 0 || anonLoading) {
      return;
    }
    if (slowMessageTimerRef.current) {
      clearTimeout(slowMessageTimerRef.current);
      slowMessageTimerRef.current = null;
    }
    setAnonMessage('');
    setAnonServiceTag(tag);
    setAnonSlowMessage('');
    startAnonCooldown();
    setAnonLoading(true);
    slowMessageTimerRef.current = setTimeout(() => {
      const choice = slowMessages[Math.floor(Math.random() * slowMessages.length)];
      setAnonSlowMessage(choice);
    }, 3000);
    try {
      const response = await fetch(`${API_BASE}/device/service-tag/${encodeURIComponent(tag)}`);
      if (!response.ok) {
        let message = 'Unable to search right now. Please try again later.';
        let data = null;
        try {
          data = await response.json();
        } catch (err) {
          data = null;
        }
        if (response.status === 404) {
          message = 'No device found for this service tag.';
        } else if (response.status === 429) {
          message = data && data.message ? data.message : 'Please wait before trying again.';
        } else if (data && data.message) {
          message = data.message;
        }
        setAnonMessage(message);
        return;
      }
      const payload = await response.json();
      const target = payload && typeof payload === 'object' && 'device' in payload ? payload.device : payload;
      if (target && target._id) {
        navigate(`/device/${target._id}`);
      } else {
        setAnonMessage('Device located but missing an identifier. Please contact support.');
      }
    } catch (err) {
      setAnonMessage('Unable to search right now. Please try again later.');
    } finally {
      if (slowMessageTimerRef.current) {
        clearTimeout(slowMessageTimerRef.current);
        slowMessageTimerRef.current = null;
      }
      setAnonSlowMessage('');
      setAnonLoading(false);
    }
  };

  useEffect(() => {
    if (!isSignedIn || !showDeviceGrid) {
      setLoading(false);
      return;
    }
    // Refetch when the series filter / test toggle changes (or on mount) while
    // keeping the current search so filtering no longer discards the search input.
    fetchDevices(true, 0, lastSearchRef.current, seriesFilter, testOnly ? 'test' : '');
  }, [isSignedIn, showDeviceGrid, fetchDevices, seriesFilter, testOnly]);

  useEffect(() => {
    if (!isSignedIn || !showDeviceGrid) return;
    if (loading) return;
    if (initialFetchRef.current) {
      initialFetchRef.current = false;
      if (!effectiveSearch) return;
    }
    if (effectiveSearch === lastSearchRef.current) return;
    const handle = setTimeout(() => {
      lastSearchRef.current = effectiveSearch;
      fetchDevices(true, 0, effectiveSearch, seriesFilter, testOnly ? 'test' : '');
    }, 300);
    return () => clearTimeout(handle);
  }, [effectiveSearch, isSignedIn, showDeviceGrid, fetchDevices, loading, seriesFilter, testOnly]);

  useEffect(() => {
    if (!isSignedIn || !showDeviceGrid) return;
    const interval = setInterval(fetchNewDevices, 5000);
    return () => clearInterval(interval);
  }, [isSignedIn, showDeviceGrid, fetchNewDevices]);

  const sortedDevices = React.useMemo(() => {
    if (loading) return [];
    const arr = [...devices];
    arr.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'scandate' || sortField === 'createdAt' || sortField === 'updatedAt') {
        const parsedA = parseDateInput(aVal);
        const parsedB = parseDateInput(bVal);
        aVal = parsedA ? parsedA.getTime() : 0;
        bVal = parsedB ? parsedB.getTime() : 0;
      } else {
        aVal = aVal ? aVal.toString().toLowerCase() : '';
        bVal = bVal ? bVal.toString().toLowerCase() : '';
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [devices, sortField, sortOrder, loading]);

  const displayedDevices = React.useMemo(() => {
    if (seriesFilter === 'all') return sortedDevices;
    const targetName = seriesMap[seriesFilter];
    return sortedDevices.filter(d => getWaveSeries(d.cusPnELU || d.productNumber) === targetName);
  }, [sortedDevices, seriesFilter]);

  const accountActions = user ? (
    <>
      <span style={accountMetaStyle}>({displayRole}) {user.emailAddresses[0]?.emailAddress}</span>
      <SignOutButton>
        <button className="ni-button ni-button--ghost" style={signOutStyle}>Sign out</button>
      </SignOutButton>
    </>
  ) : (
    <SignInButton mode="modal">
      <button className="ni-button ni-button--ghost" style={signOutStyle}>Sign in</button>
    </SignInButton>
  );

  const sharedHeader = (
    <header className="ni-header">
      <div className="ni-brand">
        <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
        <div className="ni-brand-copy">
          <span className="ni-eyebrow">North Invent Service</span>
          <span className="ni-brand-subtitle">
            {isSignedIn ? `${currentView} workspace` : 'Public device lookup'}
          </span>
        </div>
      </div>
      <div className="ni-toolbar">
        <button
          onClick={() => navigate('/wave-configurator')}
          className="ni-button ni-button--primary"
          style={waveConfiguratorButtonStyle}
        >
          Wave Configurator
        </button>
        {accountActions}
      </div>
    </header>
  );

  if (isSignedIn && isGpvRma) {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          {sharedHeader}
          <section className="ni-panel ni-panel--center">
            <h2 className="ni-section-title" style={{ marginBottom: '0.8rem' }}>Access restricted</h2>
            <p className="ni-section-copy" style={{ margin: '0 auto 1rem' }}>
              GPV-RMA accounts can open device pages directly but do not have access to the broader dashboards.
            </p>
            <p className="ni-note" style={{ margin: 0 }}>
              Use the direct device link provided in your RMA workflow or contact an administrator if you need wider permissions.
            </p>
          </section>
        </div>
      </div>
    );
  }

  if (currentView === 'GPV Quality') {
    return (
      <div className="ni-page">
        <div className="ni-shell ni-shell--narrow">
          {sharedHeader}
          <div className="ni-panel ni-panel--dense">
            <div className="ni-toolbar">
              <button onClick={() => navigate('/quality')} className="ni-button ni-button--primary" style={primaryActionStyle}>Product QMS</button>
              <button onClick={() => navigate('/products')} className="ni-button ni-button--primary" style={primaryActionStyle}>Products</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'GPV Production') {
    return <BuildOrdersPage />;
  }

  return (
    <div className="ni-page">
      <div className="ni-shell">
        {sharedHeader}
        {isSignedIn && showPocTestView && <TestGpvBanner />}

        {(isAdmin || isService) && (
          <div className="ni-panel ni-panel--dense">
            <div className="ni-toolbar">
              <button onClick={() => navigate('/rmas')} className="ni-button ni-button--primary" style={primaryActionStyle}>View RMA Dashboard</button>
              <button onClick={() => navigate('/complaints')} className="ni-button ni-button--primary" style={primaryActionStyle}>Supplier NCR</button>
              <button onClick={() => navigate('/recommission')} className="ni-button ni-button--primary" style={primaryActionStyle}>Recommission</button>
              <button onClick={() => navigate('/quality')} className="ni-button ni-button--primary" style={primaryActionStyle}>Product QMS</button>
              <button onClick={() => navigate('/support')} className="ni-button ni-button--primary" style={primaryActionStyle}>Support Tools</button>
              <button onClick={() => navigate('/rma-archive')} className="ni-button ni-button--muted" style={secondaryActionStyle}>View Archived RMAs</button>
              <button onClick={() => navigate('/build-order-archive')} className="ni-button ni-button--muted" style={secondaryActionStyle}>View Archived Orders</button>
              <button onClick={() => navigate('/analytics')} className="ni-button ni-button--primary" style={primaryActionStyle}>View Analytics</button>
              <button onClick={() => navigate('/current-stats')} className="ni-button ni-button--primary" style={primaryActionStyle}>Current Stats</button>
              <button onClick={() => navigate('/iceberg')} className="ni-button ni-button--primary" style={primaryActionStyle}>Iceberg Model</button>
              <button onClick={() => navigate('/scm')} className="ni-button ni-button--primary" style={primaryActionStyle}>SCM — Purchase Orders</button>
              <button onClick={() => navigate('/service/shipments')} className="ni-button ni-button--primary" style={primaryActionStyle}>New Shipments</button>
              <button onClick={() => navigate('/service/training')} className="ni-button ni-button--primary" style={primaryActionStyle}>Training Library</button>
              <button onClick={() => navigate('/admin/organizations')} className="ni-button ni-button--primary" style={primaryActionStyle}>Companies</button>
              {(isAdmin || isService) && <button onClick={() => navigate('/admin/users')} className="ni-button ni-button--primary" style={primaryActionStyle}>Users & Access</button>}
              <button onClick={() => navigate('/admin/backfill')} className="ni-button ni-button--primary" style={primaryActionStyle}>AI Backfill</button>
            </div>
          </div>
        )}

        {isSignedIn && (isAdmin || isService || showPocTestView) && (
          <div className="ni-panel ni-panel--dense">
            <div className="ni-toolbar" style={filterBarStyle}>
              {isAdmin && (
                <select value={viewAs} onChange={e => setViewAs(e.target.value)} className="ni-select" style={dropdownStyle}>
                  <option>Admin</option>
                  <option>guest</option>
                  <option>Test GPV</option>
                  <option>GPV Production</option>
                  <option>UTI Production</option>
                  <option>Service</option>
                </select>
              )}
              {(isService || showDeviceGrid) && (
                <>
                  <div className="ni-input" style={searchTagWrap}>
                    {searchTags.map((tag, i) => (
                      <React.Fragment key={`${tag.value}-${i}`}>
                        {i > 0 && (
                          <span style={tagJoin} title={tag.op === 'AND' ? 'AND — must match both' : 'OR — either match'}>
                            {tag.op === 'AND' ? '+' : 'or'}
                          </span>
                        )}
                        <span style={tagChip}>
                          {tag.value}
                          <button
                            type="button"
                            onClick={() => removeTag(i)}
                            style={tagRemove}
                            aria-label={`Remove ${tag.value}`}
                          >
                            ×
                          </button>
                        </span>
                      </React.Fragment>
                    ))}
                    <input
                      type="text"
                      placeholder={searchTags.length ? 'Add another… ( , = either · + = both )' : 'Search… ( , = either · + = both )'}
                      value={search}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                      style={tagTextInput}
                    />
                  </div>
                  <Link
                    to="/products"
                    className="ni-button ni-button--ghost"
                    style={{ fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    title="Browse editable product metadata by product number (not unit-specific)"
                  >
                    🗂 Products
                  </Link>
                  <button
                    type="button"
                    onClick={() => setTestOnly(v => !v)}
                    className={`ni-button ${testOnly ? 'ni-button--primary' : 'ni-button--ghost'}`}
                    style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                    title="Show only productFamily: test records"
                  >
                    🧪 Test data
                  </button>
                  {testOnly && (isAdmin || isService) && filteredCount > 0 && (
                    <button
                      type="button"
                      onClick={deleteAllTest}
                      disabled={deletingTest}
                      className="ni-button"
                      style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', color: '#fff', background: '#EC2124', border: 'none', opacity: deletingTest ? 0.6 : 1 }}
                      title="Permanently delete all test devices"
                    >
                      {deletingTest ? 'Deleting…' : `🗑 Delete all test (${filteredCount})`}
                    </button>
                  )}
                  <span className="results-info" onClick={handleReset}>
                    {`Results: ${filteredCount} (${filteredCount === totalCount ? '100%' : `${((filteredCount / totalCount) * 100).toFixed(1)}% of total`})`}
                  </span>
                  <select
                    value={seriesFilter}
                    onChange={e => setSeriesFilter(e.target.value)}
                    className="ni-select"
                    style={dropdownStyle}
                  >
                    <option value="all">All Series</option>
                    {Object.entries(seriesMap).map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={sortField}
                    onChange={e => setSortField(e.target.value)}
                    className="ni-select"
                    style={dropdownStyle}
                  >
                    <option value="scandate">Scan Date</option>
                    <option value="updatedAt">Updated</option>
                    <option value="productNumber">Product Number</option>
                    <option value="serviceTagNumber">Service Tag</option>
                  </select>
                  <select
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    className="ni-select"
                    style={dropdownStyle}
                  >
                    <option value="desc">Top to Bottom</option>
                    <option value="asc">Bottom to Top</option>
                  </select>
                </>
              )}
            </div>
          </div>
        )}

        {!isSignedIn ? (
          <section className="ni-panel ni-panel--center ni-hero">
            <span className="ni-eyebrow">Asset Lookup</span>
            <h2 className="ni-hero-title">Enter your service tag to view your product</h2>
            <p className="ni-hero-copy">Enter the exact service tag number to open your device overview.</p>
            <div style={heroForm}>
              <input
                value={anonServiceTag}
                onChange={(e) => {
                  setAnonServiceTag(e.target.value);
                  if (anonMessage) setAnonMessage('');
                }}
                className="ni-input"
                style={heroInput}
                placeholder="Service Tag Number"
                aria-label="Service Tag Number"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAnonymousSearch();
                  }
                }}
              />
              <button
                onClick={handleAnonymousSearch}
                disabled={anonLoading || anonCooldown > 0}
                className="ni-button ni-button--primary"
                style={{
                  ...heroButton,
                  opacity: anonLoading || anonCooldown > 0 ? 0.7 : 1,
                  cursor: anonLoading || anonCooldown > 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {anonCooldown > 0 ? `Please wait (${anonCooldown}s)` : 'Find my product'}
              </button>
            </div>
            <p style={{ ...heroSlowMessage, opacity: anonSlowMessage ? 1 : 0 }}>{anonSlowMessage || '\u00A0'}</p>
            {anonLoading && (
              <div style={{ marginTop: '1rem' }}>
                <LoadingSpinner size={40} />
              </div>
            )}
            {anonMessage && <p style={heroMessage}>{anonMessage}</p>}
            <p className="ni-note" style={{ margin: 0 }}>Early access</p>
          </section>
        ) : showDeviceGrid ? (
          loading && !fadeOut ? (
            <div className="ni-panel" style={loadingPanelStyle}>
              <LoadingSpinner size={64} />
            </div>
          ) : displayedDevices.length === 0 ? (
            <div className="ni-panel ni-empty">No devices found.</div>
          ) : (
            <>
              <div className={`ni-device-grid device-list${fadeOut ? ' fade-out' : ''}`}>
                {displayedDevices.map(device => (
                  <Link
                    to={showPocTestView ? `/poc-test/devices/${device._id}` : `/device/${device._id}`}
                    key={device._id}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="ni-device-card">
                      {(() => {
                        const status = getWarrantyStatus(device.scandate);
                        const color = getWarrantyColor(status);
                        return <span style={{ ...dotBaseStyle, backgroundColor: color }}></span>;
                      })()}
                      <div className="ni-device-card__media">
                        <ProductThumb
                          productNumber={device.productNumber}
                          style={imageStyle}
                        />
                      </div>
                      <div className="ni-device-card__body">
                        <strong className="ni-device-card__title device-product-number">
                          {device.productNumber}
                          {device.productFamily === 'test' && <span style={testBadgeStyle}>TEST</span>}
                        </strong>
                        <p className="ni-device-card__meta">Service Tag: {device.serviceTagNumber ?? 'N/A'}</p>
                      </div>
                      {device.productFamily === 'test' && (isAdmin || isService) && (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteTestDevice(device._id); }}
                          title="Delete this test device"
                          style={{
                            position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 2,
                            width: 26, height: 26, borderRadius: '50%', border: 'none',
                            background: '#EC2124', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: '26px', padding: 0,
                          }}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.5rem' }}>
                {loadingMore ? (
                  <LoadingSpinner />
                ) : (
                  hasMore && (
                    <button onClick={() => fetchDevices(false, devices.length, effectiveSearch, seriesFilter, testOnly ? 'test' : '')} className="ni-button ni-button--ghost" style={showMoreButton}>
                      More devices
                    </button>
                  )
                )}
              </div>
            </>
          )
        ) : currentView === 'Admin' ? (
          <div className="ni-panel ni-empty">Work in progress.</div>
        ) : currentView === 'guest' ? (
          <div className="ni-panel ni-empty">You do not have permission to view this page.</div>
        ) : (
          <div className="ni-panel ni-empty">Coming soon...</div>
        )}
      </div>
    </div>
  );
}

// --- Styling ---
const heroForm = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  width: '100%',
  maxWidth: '420px'
};

const heroInput = {
  maxWidth: '420px'
};

const heroButton = {
  width: '100%',
  justifyContent: 'center'
};

const heroMessage = {
  color: colors.textStrong,
  fontWeight: '500'
};

const heroSlowMessage = {
  minHeight: '1.25rem',
  color: 'rgba(255,255,255,0.65)',
  fontSize: '0.9rem',
  margin: 0,
  transition: 'opacity 0.3s ease'
};

const primaryActionStyle = {
  minHeight: '44px'
};

const secondaryActionStyle = {
  minHeight: '44px'
};

const searchStyle = {
  width: '100%',
  minWidth: 'min(360px, 100%)',
  maxWidth: '560px'
};

const searchTagWrap = {
  ...searchStyle,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '0.35rem',
  cursor: 'text'
};

const tagJoin = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#94a3b8'
};

const tagChip = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  background: 'rgba(70,187,137,0.16)',
  border: '1px solid rgba(70,187,137,0.4)',
  borderRadius: '999px',
  padding: '0.1rem 0.5rem',
  fontSize: '0.8rem',
  color: '#46BB89',
  whiteSpace: 'nowrap'
};

const tagRemove = {
  background: 'none',
  border: 'none',
  color: '#46BB89',
  cursor: 'pointer',
  fontSize: '0.95rem',
  lineHeight: 1,
  padding: 0
};

const tagTextInput = {
  flex: 1,
  minWidth: '120px',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'inherit',
  fontSize: 'inherit',
  padding: 0
};

const dotBaseStyle = {
  position: 'absolute',
  top: '14px',
  right: '14px',
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: colors.accent
};

const imageStyle = {
  width: '120px',
  height: '120px',
  objectFit: 'contain',
  borderRadius: '6px'
};

const accountMetaStyle = {
  color: colors.textMuted,
  fontSize: '0.9rem'
};

const signOutStyle = {
  minHeight: '44px'
};

const waveConfiguratorButtonStyle = {
  whiteSpace: 'nowrap'
};

const filterBarStyle = {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap'
};

const dropdownStyle = {
  width: 'auto',
  minWidth: '170px'
};

const showMoreButton = {
  minWidth: '180px'
};

const testBadgeStyle = {
  display: 'inline-block',
  marginLeft: '0.4rem',
  padding: '0.05rem 0.4rem',
  borderRadius: '4px',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  verticalAlign: 'middle',
  background: 'rgba(234,179,8,0.18)',
  border: '1px solid rgba(234,179,8,0.5)',
  color: '#eab308',
};

const loadingPanelStyle = {
  minHeight: '50vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};


